/**
 * lac.ts — Categoría "Lácteos" (10 productos demo).
 * Insumos clave para recetas de restaurante, pastelería y heladería.
 */
import { buildProduct } from "./_factory";
import type { Producto } from "@/types";

const CAT = {
  categoryId: "cat_lac",
  skuPrefix: "LAC",
  categoria: "Lácteos",
} as const;

export const cat_lac_products: Producto[] = [
  buildProduct({
    ...CAT,
    index: 0,
    nombre: "Leche Entera Alpina 1L",
    marca: "Alpina",
    modelo: "Entera UHT",
    origen: "Colombia",
    costo: 3200,
    precio: 4800,
    stock: 150,
    stockMinimo: 30,
    stockMaximo: 280,
    unidad: "litro",
    ubicacionFisica: "Nevera 1, lácteos frescos",
    proveedor: "Alpina Productos Alimenticios",
    tiempoEntrega: "Diario",
    vencimientoMeses: 3,
    pesoNeto: "1L",
    descripcionLarga:
      "Leche entera ultrapasteurizada, rica en calcio y proteína. Ideal para consumo directo, repostería, café y salsas.\n\nEmpaque Tetra Pak que garantiza frescura sin refrigeración hasta abrir.",
    especificaciones: [
      { clave: "Tipo", valor: "Entera UHT" },
      { clave: "Grasa", valor: "3.2%" },
    ],
    imageKeywords: ["milk", "leche", "dairy"],
    color: "bg-white",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 1,
    nombre: "Queso Mozzarella Colanta 500g",
    marca: "Colanta",
    modelo: "Mozzarella Pizza",
    origen: "Colombia",
    costo: 11000,
    precio: 16500,
    stock: 85,
    stockMinimo: 18,
    stockMaximo: 160,
    unidad: "bolsa",
    ubicacionFisica: "Nevera 2, quesos",
    proveedor: "Cooperativa Colanta",
    vencimientoMeses: 2,
    pesoNeto: "500g",
    descripcionLarga:
      "Queso mozzarella rallado listo para hornear. Derrite uniforme, dora sin quemarse. Ideal para pizza, lasagna y gratinados.",
    especificaciones: [
      { clave: "Formato", valor: "Rallado" },
      { clave: "Uso", valor: "Pizza / gratinar" },
    ],
    imageKeywords: ["mozzarella", "cheese", "queso"],
    color: "bg-yellow-50",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 2,
    nombre: "Queso Cheddar Lonchas 200g",
    marca: "Alpina",
    modelo: "Cheddar Tajado",
    origen: "Colombia",
    costo: 8500,
    precio: 12800,
    stock: 70,
    stockMinimo: 15,
    stockMaximo: 140,
    unidad: "paquete",
    ubicacionFisica: "Nevera 2, quesos",
    proveedor: "Alpina Productos Alimenticios",
    vencimientoMeses: 2,
    pesoNeto: "200g",
    descripcionLarga:
      "Queso cheddar amarillo en lonchas individuales. Sabor intenso, textura cremosa. Perfecto para hamburguesas y sándwiches.",
    especificaciones: [
      { clave: "Formato", valor: "12 lonchas" },
      { clave: "Uso", valor: "Hamburguesa / sándwich" },
    ],
    imageKeywords: ["cheddar", "cheese-slice", "queso-amarillo"],
    color: "bg-orange-400",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 3,
    nombre: "Mantequilla Alpina 250g",
    marca: "Alpina",
    modelo: "Con Sal",
    origen: "Colombia",
    costo: 7200,
    precio: 10900,
    stock: 60,
    stockMinimo: 12,
    stockMaximo: 120,
    unidad: "barra",
    ubicacionFisica: "Nevera 1, lácteos",
    proveedor: "Alpina Productos Alimenticios",
    vencimientoMeses: 4,
    pesoNeto: "250g",
    descripcionLarga:
      "Mantequilla con sal, elaborada con crema de leche pasteurizada. Textura cremosa, derretido parejo para pastelería y cocción.",
    especificaciones: [
      { clave: "Grasa", valor: "80%" },
      { clave: "Contenido", valor: "Con sal" },
    ],
    imageKeywords: ["butter", "mantequilla", "dairy"],
    color: "bg-yellow-200",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 4,
    nombre: "Crema de Leche Alpina 200ml",
    marca: "Alpina",
    modelo: "Espesa",
    origen: "Colombia",
    costo: 3800,
    precio: 5700,
    stock: 110,
    stockMinimo: 25,
    stockMaximo: 200,
    unidad: "unidad",
    ubicacionFisica: "Nevera 1, lácteos",
    proveedor: "Alpina Productos Alimenticios",
    vencimientoMeses: 3,
    pesoNeto: "200ml",
    descripcionLarga:
      "Crema de leche espesa, ideal para salsas, sopas, helados caseros y postres. Monta con batidora en 3 minutos.",
    especificaciones: [
      { clave: "Grasa", valor: "35%" },
      { clave: "Formato", valor: "Bolsa 200ml" },
    ],
    imageKeywords: ["heavy-cream", "crema-de-leche", "dairy"],
    color: "bg-white",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 5,
    nombre: "Yogurt Natural Alpina 1L",
    marca: "Alpina",
    modelo: "Finesse Natural",
    origen: "Colombia",
    costo: 5600,
    precio: 8400,
    stock: 95,
    stockMinimo: 20,
    stockMaximo: 180,
    unidad: "litro",
    ubicacionFisica: "Nevera 1, lácteos",
    proveedor: "Alpina Productos Alimenticios",
    vencimientoMeses: 2,
    pesoNeto: "1L",
    descripcionLarga:
      "Yogurt natural entero con cultivos vivos. Consistencia cremosa, sabor suave. Ideal para desayunos, smoothies y salsas blancas.",
    especificaciones: [
      { clave: "Tipo", valor: "Natural entero" },
      { clave: "Probióticos", valor: "Lactobacillus" },
    ],
    imageKeywords: ["yogurt", "yoghurt", "natural-yogurt"],
    color: "bg-stone-100",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 6,
    nombre: "Huevos AA Kikes x30",
    marca: "Kikes",
    modelo: "Rojos AA",
    origen: "Colombia",
    costo: 14500,
    precio: 21000,
    stock: 55,
    stockMinimo: 12,
    stockMaximo: 110,
    unidad: "panal",
    ubicacionFisica: "Nevera 1, huevos",
    proveedor: "Huevos Kikes S.A.",
    vencimientoMeses: 1,
    pesoNeto: "1.8kg",
    descripcionLarga:
      "Panal de 30 huevos rojos calidad AA. Gallinas alimentadas con maíz, sin hormonas. Tamaño uniforme para repostería profesional.",
    especificaciones: [
      { clave: "Categoría", valor: "AA" },
      { clave: "Cantidad", valor: "30 unidades" },
    ],
    imageKeywords: ["eggs", "huevos", "egg-carton"],
    color: "bg-amber-200",
    shape: "circle",
  }),
  buildProduct({
    ...CAT,
    index: 7,
    nombre: "Leche Condensada La Lechera 395g",
    marca: "Nestlé",
    modelo: "La Lechera",
    origen: "Colombia",
    costo: 6200,
    precio: 9400,
    stock: 100,
    stockMinimo: 20,
    stockMaximo: 180,
    unidad: "lata",
    ubicacionFisica: "Estante B1, lácteos procesados",
    proveedor: "Nestlé de Colombia",
    vencimientoMeses: 18,
    pesoNeto: "395g",
    descripcionLarga:
      "Leche condensada azucarada, base para tres leches, flan, arequipe y postres tradicionales. Abre-fácil, listo para verter.",
    especificaciones: [
      { clave: "Presentación", valor: "Lata 395g" },
      { clave: "Uso", valor: "Repostería" },
    ],
    imageKeywords: ["condensed-milk", "leche-condensada", "dessert"],
    color: "bg-amber-50",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 8,
    nombre: "Queso Campesino Fresco 500g",
    marca: "Alquería",
    modelo: "Campesino",
    origen: "Colombia",
    costo: 9800,
    precio: 14900,
    stock: 65,
    stockMinimo: 15,
    stockMaximo: 130,
    unidad: "bloque",
    ubicacionFisica: "Nevera 2, quesos",
    proveedor: "Lácteos Alquería",
    vencimientoMeses: 1,
    pesoNeto: "500g",
    descripcionLarga:
      "Queso campesino fresco, ligeramente salado, textura firme. Ideal para arepa, almojábana, pandebono y desayuno tradicional.",
    especificaciones: [
      { clave: "Textura", valor: "Fresca firme" },
      { clave: "Sal", valor: "Media" },
    ],
    imageKeywords: ["fresh-cheese", "queso-campesino", "white-cheese"],
    color: "bg-white",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 9,
    nombre: "Yogurt Griego Alpina 150g",
    marca: "Alpina",
    modelo: "Regeneris Griego",
    origen: "Colombia",
    costo: 2800,
    precio: 4300,
    stock: 140,
    stockMinimo: 30,
    stockMaximo: 260,
    unidad: "vaso",
    ubicacionFisica: "Nevera 1, lácteos",
    proveedor: "Alpina Productos Alimenticios",
    vencimientoMeses: 2,
    pesoNeto: "150g",
    descripcionLarga:
      "Yogurt griego con alto contenido proteico, textura espesa y cremosa. Sabor natural, ideal con frutas y granola.",
    especificaciones: [
      { clave: "Proteína", valor: "10g por vaso" },
      { clave: "Formato", valor: "Vaso 150g" },
    ],
    imageKeywords: ["greek-yogurt", "yogurt-griego", "healthy-snack"],
    color: "bg-stone-50",
    shape: "circle",
  }),
];
